import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Compass, Hammer, ClipboardCheck } from "lucide-react";
import {
    PRECON_SERVICES,
    CONSTRUCTION_SERVICES,
    DELIVERY_METHODS,
    TEXTURE_BG,
} from "../lib/data";

const Services = () => {
    return (
        <main data-testid="services-page" className="bg-background">
            {/* HEADER */}
            <section className="relative px-6 md:px-12 lg:px-24 pt-24 md:pt-32 pb-16 border-b border-border overflow-hidden">
                <img
                    src={TEXTURE_BG}
                    alt=""
                    className="absolute inset-0 w-full h-full object-cover opacity-10 pointer-events-none"
                />
                <div className="relative">
                    <div className="font-mono text-xs tracking-[0.3em] uppercase text-accent mb-4">
                        Services
                    </div>
                    <h1 className="font-display font-black text-5xl sm:text-7xl lg:text-8xl uppercase tracking-tighter leading-[0.85]">
                        Listen. Plan.
                        <br />
                        <span className="text-accent">Build.</span>
                    </h1>
                    <p className="mt-8 max-w-2xl text-lg text-muted-foreground">
                        From initial planning to final completion, we lead with teamwork and
                        deliver every detail with precision and purpose.
                    </p>
                </div>
            </section>

            {/* PRECON */}
            <section className="px-6 md:px-12 lg:px-24 py-24 md:py-32">
                <div className="grid md:grid-cols-12 gap-12">
                    <div className="md:col-span-4">
                        <div className="flex items-center gap-3 mb-6">
                            <Compass className="w-7 h-7 text-accent" />
                            <span className="font-mono text-xs tracking-[0.3em] uppercase text-muted-foreground">
                                01 / Phase
                            </span>
                        </div>
                        <h2 className="font-display font-black text-4xl sm:text-5xl uppercase tracking-tight leading-[0.9]">
                            Pre-
                            <br /> Construction
                        </h2>
                        <p className="mt-6 text-muted-foreground max-w-sm">
                            We engage early. We listen first. We plan together. The result: fewer
                            surprises, lower costs, faster occupancy.
                        </p>
                    </div>
                    <div className="md:col-span-8 grid sm:grid-cols-2 gap-px bg-background border border-border">
                        {PRECON_SERVICES.map((s, i) => (
                            <div
                                key={s.title}
                                data-testid={`precon-${i}`}
                                className="bg-card p-6 md:p-8"
                            >
                                <div className="font-mono text-xs text-accent">0{i + 1}</div>
                                <div className="font-display font-bold text-xl uppercase tracking-tight mt-3">
                                    {s.title}
                                </div>
                                <div className="mt-2 text-sm text-muted-foreground leading-relaxed">
                                    {s.outcome}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CONSTRUCTION */}
            <section className="bg-secondary text-secondary-foreground">
                <div className="px-6 md:px-12 lg:px-24 py-24 md:py-32">
                    <div className="grid md:grid-cols-12 gap-12">
                        <div className="md:col-span-4">
                            <div className="flex items-center gap-3 mb-6">
                                <Hammer className="w-7 h-7 text-accent" />
                                <span className="font-mono text-xs tracking-[0.3em] uppercase text-secondary-foreground/60">
                                    02 / Phase
                                </span>
                            </div>
                            <h2 className="font-display font-black text-4xl sm:text-5xl uppercase tracking-tight leading-[0.9]">
                                Construction
                            </h2>
                            <p className="mt-6 text-secondary-foreground/70 max-w-sm">
                                Boots on the ground. Daily reports. Weekly safety reviews. Clash
                                detection that catches problems before they reach the field.
                            </p>
                        </div>
                        <div className="md:col-span-8 grid sm:grid-cols-2 gap-px bg-secondary-foreground/15 border border-secondary-foreground/15">
                            {CONSTRUCTION_SERVICES.map((s, i) => (
                                <div
                                    key={s}
                                    data-testid={`construction-${i}`}
                                    className="bg-secondary p-6 md:p-8"
                                >
                                    <div className="font-mono text-xs text-accent">0{i + 1}</div>
                                    <div className="font-display font-bold text-xl uppercase tracking-tight mt-3">
                                        {s}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* DELIVERY METHODS */}
            <section className="px-6 md:px-12 lg:px-24 py-24 md:py-32">
                <div className="flex items-center gap-3 mb-6">
                    <ClipboardCheck className="w-7 h-7 text-accent" />
                    <span className="font-mono text-xs tracking-[0.3em] uppercase text-muted-foreground">
                        03 / Delivery
                    </span>
                </div>
                <h2 className="font-display font-black text-4xl sm:text-5xl uppercase tracking-tight leading-[0.9] max-w-2xl">
                    Every delivery method.
                    <br /> One standard of work.
                </h2>
                <div className="mt-12 flex flex-wrap gap-2 max-w-4xl">
                    {DELIVERY_METHODS.map((m, i) => (
                        <span
                            key={m}
                            data-testid={`delivery-${i}`}
                            className="border border-border bg-card px-5 py-3 font-mono text-sm uppercase tracking-wider"
                        >
                            {m}
                        </span>
                    ))}
                </div>

                <div className="mt-20 border-t border-border pt-12 flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
                    <div className="font-display font-black text-2xl md:text-3xl uppercase tracking-tight max-w-md">
                        Have a project? Let's talk scope.
                    </div>
                    <Link
                        to="/contact"
                        data-testid="services-cta"
                        className="group inline-flex items-center gap-2 bg-accent text-accent-foreground px-6 py-4 font-bold uppercase tracking-wider hover:bg-foreground transition-colors"
                    >
                        Request bid package
                        <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </Link>
                </div>
            </section>
        </main>
    );
};

export default Services;
